import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { isGUID } from "@bps/common-utils";
import { NoDataTile, Stack, useTheme } from "@bps/fluent-ui";
import { withPermissions } from "@components/withPermissions";
import { BpIdUsersSearchArgs } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useBusinessUserSearch } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { BusinessUsersFilter } from "./BusinessUsersFilter";
import { BusinessUsersTable } from "./BusinessUsersTable";

const BusinessUsersBase: React.FC = () => {
  const theme = useTheme();
  // Keep the search term in the URL so navigating into an account and back
  // restores the results (card 57898 requirement).
  const [searchParams, setSearchParams] = useSearchParams();
  const text = searchParams.get("q") ?? "";

  const filter: BpIdUsersSearchArgs | undefined = useMemo(
    () => (text ? { text } : undefined),
    [text]
  );

  const { data, isLoading, error } = useBusinessUserSearch(filter);

  const errorMessage = useMemo(() => {
    if (error) return error?.message;
    if (data?.tooManyResults)
      return "Your search returned too many results, please refine your search parameters.";
    if (data?.errorMessage) return data.errorMessage;
    return undefined;
  }, [data?.errorMessage, data?.tooManyResults, error]);

  const sourceType = source => {
    if (source.includes("@")) return "username";
    if (isGUID(source)) return "object id";
    return "name";
  };

  const generateEmptyText = (source?: string) => {
    if (source) {
      return `No match for "${source}" in ${sourceType(source)}`;
    }
    return "Search by ONE of object ID, username, or account name (not Premier name)";
  };
  return (
    <Stack verticalFill tokens={{ childrenGap: theme.spacing.m }}>
      <BusinessUsersFilter
        initialText={text}
        onFilter={values =>
          setSearchParams(values.text ? { q: values.text } : {})
        }
        onReset={() => setSearchParams({})}
      />
      <Stack verticalFill>
        {data?.records?.length || isLoading ? (
          <BusinessUsersTable
            enableShimmer={isLoading}
            errorMessage={errorMessage}
            items={data?.records || []}
          />
        ) : (
          <NoDataTile
            textProps={{
              text: generateEmptyText(filter?.text)
            }}
            linkProps={{ hidden: true }}
            showBoxShadow={false}
          />
        )}
      </Stack>
    </Stack>
  );
};

export const BusinessUsers = withPermissions(
  BusinessUsersBase,
  [
    Permissions.PltBpidUserRead,
    Permissions.PltBpidUserWrite,
    Permissions.PltBpidUserClear,
    Permissions.PltBpidMfaDelete
  ],
  "or"
);
